/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Switch } from "../../ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
  Search,
  Edit,
  Trash2,
  UserPlus,
  Shield,
  UserCheck,
  Clock,
  XCircle,
  Calendar,
  CheckCircle,
  Filter,
  X,
  Maximize2,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import {
  getUsers,
  getRoles,
  createUser,
  updateUser,
  getDetailsByITS,
  getUserById,
  toggleUserStatus,
  RoleResponse,
  CreateUserPayload,
  softDeleteUser,
  getDashboardSummary,
  DashboardSummary,
  getVenues,
  VenueResponse,
} from "../../services/userService";
import { Checkbox } from "../../ui/checkbox";
import Pagination from "../../ui/pagination";
import { useScreenPermission } from "../../hooks/useScreenPermission";
import { FullScreenLoading } from "components/ui/loader";
import { CheckboxFieldset } from "ui/checkboxFieldset";
import { usePageHeader } from "contexts/PageHeaderContext";
import { useAuth } from "contexts/AuthContext";

interface SystemUser {
  id: string;
  name: string;
  email: string;
  mobileNo?: string;
  whatsAppNumber?: string;
  officeName?: string;
  userCode: string;

  roleIds?: number[];
  backendRoles?: {
    roleId: number;
    roleName: string;
  }[];

  department?: string;
  isActive: boolean;
  createdDate?: string;
  expiryDate?: string;
  itsNumber?: string;
  venue?: string;
  package?: string;
}

interface TemporaryUser {
  id: string;
  itsNumber?: string;
  userCode: string;
  name: string;
  email: string;
  phone?: string;
  venue?: string;
  package?: string;
  requestedRole: string;
  expiryDate?: string;
  remark: string;
  requestedDate: string;
  requestedBy?: string;
  status: "pending" | "approved";
  roles?: string[];
}

interface RejectedUser {
  requestedBy: string;
  id: string;
  itsNumber?: string;
  name: string;
  email: string;
  phone?: string;
  venue?: string;
  package?: string;
  requestedRole: string;
  remark: string;
  requestedDate: string;
  rejectedDate: string;
  rejectionReason: string;
  rejectedBy: string;
}

const loadTemporaryUsersFromStorage = (): TemporaryUser[] => {
  try {
    const requests = localStorage.getItem("userRequests");
    if (!requests) {
      return [];
    }
    const parsed = JSON.parse(requests);
    if (!Array.isArray(parsed)) {
      console.error("Invalid user requests data format");
      return [];
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (
      parsed
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((req: any) => {
          if (!req || typeof req !== "object") {
            return null;
          }
          // Handle roles mapping safely
          let requestedRole = "No Role Selected";
          if (req.roles && Array.isArray(req.roles) && req.roles.length > 0) {
            requestedRole = req.requestedRole || req.roles.join(", ");
          }
          return {
            id: req.id || `req-${Date.now()}-${Math.random()}`,
            itsNumber: req.itsNumber || "",
            name: req.name || "",
            email: req.email || "",
            phone: req.phone || "",
            venue: req.venue || "",
            package: req.package || "",
            requestedRole: requestedRole,
            expiryDate: req.expiryDate || "",
            remark: req.remark || "",
            requestedDate:
              req.requestedDate || new Date().toISOString().split("T")[0],
            requestedBy: req.requestedBy || "Unknown",
            status: req.status || "pending",
            roles: Array.isArray(req.roles) ? req.roles : [],
          };
        })
        .filter(Boolean) as TemporaryUser[]
    );
  } catch (error) {
    console.error("Error loading user requests:", error);
    return [];
  }
};

const initialTemporaryUsers: TemporaryUser[] = loadTemporaryUsersFromStorage();

// Initial rejected users
const initialRejectedUsers: RejectedUser[] = [];

export const UserManagementPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = Boolean(user?.isSuperAdmin);

  const { setPageHeader } = usePageHeader();
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [temporaryUsers, setTemporaryUsers] = useState<TemporaryUser[]>(
    initialTemporaryUsers,
  );
  const [rejectedUsers, setRejectedUsers] =
    useState<RejectedUser[]>(initialRejectedUsers);
  const [activeTab, setActiveTab] = useState("active-users");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<SystemUser | null>(null);
  const [editingTempUser, setEditingTempUser] = useState<TemporaryUser | null>(
    null,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditingTemp, setIsEditingTemp] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState(false);
  const [tempUserToReject, setTempUserToReject] =
    useState<TemporaryUser | null>(null);
  const [activeUserToDelete, setActiveUserToDelete] =
    useState<SystemUser | null>(null);
  const [rejectionRemark, setRejectionRemark] = useState("");

  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [activeUserToToggle, setActiveUserToToggle] =
    useState<SystemUser | null>(null);

  const [showFilters, setShowFilters] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [rolesFromApi, setRolesFromApi] = useState<RoleResponse[]>([]);

  const [isFetchingIts, setIsFetchingIts] = useState(false);
  const [hasFetchedIts, setHasFetchedIts] = useState(false);
  const [formIsNonItsUser, setFormIsNonItsUser] = useState(false);
  const [isItsVerified, setIsItsVerified] = useState(false);
  const [itsNotFound, setItsNotFound] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // const [totalUsersCount, setTotalUsersCount] = useState(0);

  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const {
    canAdd,
    canEdit,
    canDelete,
    screenName,
    icon: ScreenIcon,
  } = useScreenPermission();
  const [loading, setLoading] = useState(false);
  const [savingUser, setSavingUser] = useState(false);

  const [dashboardSummary, setDashboardSummary] =
    useState<DashboardSummary | null>(null);
  const [venues, setVenues] = useState<VenueResponse[]>([]);

  const genericRoles = rolesFromApi.filter(
    (r) => r.isActive && !r.isAvailableForDelegation,
  );

  const delegationRoles = rolesFromApi.filter(
    (r) => r.isActive && r.isAvailableForDelegation,
  );

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    name: "",
    email: "",
    role: "all",
    status: "all",
    itsNumber: "",
  });

  // Form state
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formWhatsapp, setFormWhatsapp] = useState("");
  const [formRoles, setFormRoles] = useState<number[]>([]);
  const [formDepartment, setFormDepartment] = useState("");
  const [formIsActive, setFormIsActive] = useState(true);
  const [formCanRequestUser, setFormCanRequestUser] = useState(false);
  const [formExpiryDate, setFormExpiryDate] = useState("");
  const [formItsNumber, setFormItsNumber] = useState("");
  const [formVenue, setFormVenue] = useState("");
  const [formPackage, setFormPackage] = useState("");
  const [formEvent, setFormEvent] = useState("");
  const [formRemark, setFormRemark] = useState("");
  const [isRolesMaximized, setIsRolesMaximized] = useState(false);

  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    const refreshTemporaryUsers = () => {
      const loadedUsers = loadTemporaryUsersFromStorage();
      setTemporaryUsers(loadedUsers);
    };
    if (activeTab === "temporary-users") {
      refreshTemporaryUsers();
    }
    refreshTemporaryUsers();
  }, [activeTab]);

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const summary = await getDashboardSummary();
        setDashboardSummary(summary);
      } catch (error) {
        console.error("Failed to load dashboard summary", error);
        toast.error("Failed to load statistics");
      }
    };

    loadSummary();
  }, []);

  useEffect(() => {
    const loadVenues = async () => {
      try {
        const res = await getVenues(1, 100);
        setVenues(res.items ?? []);
      } catch (error) {
        console.error("Failed to load venues", error);
        toast.error("Failed to load venues");
      }
    };

    loadVenues();
  }, []);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await getUsers(1, 1000);
      const baseUsers = res.users ?? [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const usersWithRoles: SystemUser[] = baseUsers.map((u: any) => ({
        id: String(u.userId),
        userCode: u.userCode,
        name: u.fullName,
        email: u.email,
        mobileNo: u.mobileNo,
        whatsAppNumber: u.whatsAppNumber,
        officeName: u.officeName,
        roleIds: u.roleIdsArray ?? [],
        backendRoles:
          u.roleIdsArray?.map((id: number, index: number) => ({
            roleId: id,
            roleName: u.roleNamesArray?.[index],
          })) ?? [],
        isActive: u.status === "Active",
        createdDate: u.validityStartDate?.split("T")[0],
        expiryDate: u.validityEndDate?.split("T")[0],
        itsNumber: u.itsNo,
      }));

      setUsers(usersWithRoles);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users
    .filter((user) => {
      const globalSearch = filters.search.toLowerCase();
      const matchesGlobal =
        !filters.search ||
        user.name.toLowerCase().includes(globalSearch) ||
        user.email.toLowerCase().includes(globalSearch) ||
        user.itsNumber?.includes(globalSearch);
      const matchesName =
        !filters.name ||
        user.name.toLowerCase().includes(filters.name.toLowerCase());
      const matchesEmail =
        !filters.email ||
        user.email.toLowerCase().includes(filters.email.toLowerCase());
      const matchesITS =
        !filters.itsNumber || user.itsNumber?.includes(filters.itsNumber);
      const matchesStatus =
        filters.status === "all" ||
        (filters.status === "active" && user.isActive) ||
        (filters.status === "inactive" && !user.isActive);
      const matchesRole =
        filters.role === "all" || user.roleIds?.includes(Number(filters.role));
      return (
        matchesGlobal &&
        matchesName &&
        matchesEmail &&
        matchesITS &&
        matchesStatus &&
        matchesRole
      );
    })
    .sort((a, b) => {
      if (!sortColumn) return 0;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let aValue: any;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let bValue: any;

      if (sortColumn === "name") {
        aValue = a.name || "";
        bValue = b.name || "";
      }

      if (sortColumn === "email") {
        aValue = a.email || "";
        bValue = b.email || "";
      }

      if (sortColumn === "role") {
        aValue = a.backendRoles?.[0]?.roleName || "";
        bValue = b.backendRoles?.[0]?.roleName || "";
      }

      return sortDirection === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });

  const filteredTempUsers = temporaryUsers.filter((user) => {
    const matchesName =
      !filters.name ||
      user.name.toLowerCase().includes(filters.name.toLowerCase());

    const matchesEmail =
      !filters.email ||
      user.email.toLowerCase().includes(filters.email.toLowerCase());

    const matchesITS =
      !filters.itsNumber || user.itsNumber?.includes(filters.itsNumber);

    return matchesName && matchesEmail && matchesITS;
  });

  const filteredRejectedUsers = rejectedUsers.filter((user) => {
    const matchesName =
      !filters.name ||
      user.name.toLowerCase().includes(filters.name.toLowerCase());

    const matchesEmail =
      !filters.email ||
      user.email.toLowerCase().includes(filters.email.toLowerCase());

    const matchesITS =
      !filters.itsNumber || user.itsNumber?.includes(filters.itsNumber);

    return matchesName && matchesEmail && matchesITS;
  });

  const hasLoaded = useRef(false);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const res = await getRoles(1, 100);
        setRolesFromApi(res.roles ?? []);
      } catch (error) {
        console.error("Failed to load roles", error);
        toast.error("Failed to load roles");
      }
    };

    loadRoles();
  }, []);

  useEffect(() => {
    if (formIsNonItsUser && formItsNumber === "99999999") {
      return;
    }
    if (formItsNumber.length === 8 && !editingUser) {
      fetchItsDetails(formItsNumber);
    }
    if (formItsNumber.length !== 8 && !editingUser) {
      setIsItsVerified(false);
      setItsNotFound(false);
      setFormName("");
      setFormEmail("");
      setFormPhone("");
      setFormWhatsapp("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formItsNumber, formIsNonItsUser, editingUser]);

  const handleOpenDialog = async (user?: SystemUser) => {
    setIsEditingTemp(false);
    setErrors({});
    if (user) {
      try {
        setLoading(true);
        setEditingUser(user);
        const userDetails = await getUserById(Number(user.id));
        setFormName(userDetails.fullName || "");
        setFormEmail(userDetails.email || "");
        setFormPhone(userDetails.mobileNo || "");
        setFormWhatsapp(userDetails.whatsAppNumber || "");
        setFormItsNumber(userDetails.itsNo || "");
        setFormIsActive(userDetails.status === "Active");
        setFormExpiryDate(
          userDetails.validityEndDate
            ? userDetails.validityEndDate.split("T")[0]
            : "",
        );
        setFormVenue(
          userDetails.venueId !== null && userDetails.venueId !== undefined
            ? String(userDetails.venueId)
            : "",
        );
        setFormRoles(
          Array.isArray(userDetails.roleIdsArray)
            ? userDetails.roleIdsArray
            : user.roleIds || [],
        );
        if (userDetails.itsNo && userDetails.itsNo !== "99999999") {
          setFormIsNonItsUser(false);
          setIsItsVerified(true);
          setItsNotFound(false);
          setHasFetchedIts(true);
        } else if (userDetails.itsNo === "99999999") {
          setFormIsNonItsUser(true);
          setIsItsVerified(false);
          setItsNotFound(false);
          setHasFetchedIts(false);
        } else {
          setFormIsNonItsUser(false);
          setIsItsVerified(false);
          setItsNotFound(false);
          setHasFetchedIts(false);
        }
        setFormDepartment("");
        setFormPackage("");
        setFormEvent("");
        setFormRemark("");

        setIsDialogOpen(true);
      } catch (error) {
        console.error("Failed to load user details:", error);
        toast.error("Failed to load user details");
      } finally {
        setLoading(false);
      }
      return;
    }
    setEditingUser(null);
    setFormName("");
    setFormEmail("");
    setFormPhone("");
    setFormWhatsapp("");
    setFormRoles([]);
    setFormDepartment("");
    setFormIsActive(true);
    setFormExpiryDate("");
    setFormItsNumber("");
    setFormIsNonItsUser(false);
    setIsItsVerified(false);
    setItsNotFound(false);
    setHasFetchedIts(false);
    setFormVenue("");
    setFormPackage("");
    setFormEvent("");
    setFormRemark("");
    setIsDialogOpen(true);
  };

  const handleOpenTempUserDialog = (tempUser: TemporaryUser) => {
    setIsEditingTemp(true);
    setEditingTempUser(tempUser);
    setFormName(tempUser.name);
    setFormEmail(tempUser.email);
    setFormPhone(tempUser.phone || "");
    setFormItsNumber(tempUser.itsNumber || "");
    setFormPhone(tempUser.phone || "");
    setFormVenue(tempUser.venue || "");
    setFormPackage(tempUser.package || "");
    setFormExpiryDate(tempUser.expiryDate || "");
    setFormRemark(tempUser.remark);
    setFormRoles(tempUser.roles?.map(Number) || []);
    setTimeout(() => setIsDialogOpen(true), 0);
  };

  const validateForm = () => {
    const isNonITS = formIsNonItsUser;
    const canEditContactDetails = isNonITS || isSuperAdmin;
    const newErrors: Record<string, string> = {};
    if (!formItsNumber.trim()) {
      newErrors.formItsNumber = "ITS Number is required";
    } else if (formItsNumber.length !== 8) {
      newErrors.formItsNumber = "ITS Number must be 8 digits";
    } else if (!formIsNonItsUser && itsNotFound) {
      newErrors.formItsNumber = "No record found for this ITS number.";
    }

    // ONLY validate these manually for Non ITS users
    if (isNonITS) {
      if (!formName.trim()) {
        newErrors.formName = "Full Name is required";
      } else if (!/^[A-Za-z\s]+$/.test(formName)) {
        newErrors.formName = "Full Name can contain only letters and spaces";
      }
    }

    if (canEditContactDetails) {
      if (!formEmail.trim()) {
        newErrors.formEmail = "Email is required";
      } else if (
        !/^[A-Za-z0-9._]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formEmail)
      ) {
        newErrors.formEmail =
          "Only letters, numbers, '.', '_' and '@' are allowed in the email address";
      }

      if (!formPhone.trim()) {
        newErrors.formPhone = "Mobile Number is required";
      } else if (!/^\+\d{1,15}$|^\d{1,16}$/.test(formPhone)) {
        newErrors.formPhone =
          "Enter a valid mobile number with country code. Example: +91XXXXXXXX01";
      }
    }

    if (formWhatsapp.trim() && !/^\+\d{1,15}$|^\d{1,16}$/.test(formWhatsapp)) {
      newErrors.formWhatsapp =
        "Enter a valid WhatsApp number with country code. Example: +91XXXXXXXX01";
    }

    if (formRoles.length === 0) {
      newErrors.formRoles = "Please select at least one role";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateUserCode = () => {
    return "USR" + Date.now();
  };

  const getRoleColor = (roleName: string) => {
    const role = roleName.toLowerCase().replace(/\s+/g, "-");

    switch (role) {
      case "super-admin":
        return "bg-purple-100 text-purple-800 border-purple-200";

      case "admin":
      case "administrator-test":
        return "bg-blue-100 text-blue-800 border-blue-200";

      case "finance-head":
        return "bg-green-100 text-green-800 border-green-200";

      case "hr-team":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";

      case "tour-operator":
        return "bg-indigo-100 text-indigo-800 border-indigo-200";

      case "hotel-manager":
        return "bg-pink-100 text-pink-800 border-pink-200";

      case "test":
        return "bg-orange-100 text-orange-800 border-orange-200";

      case "muwafiqa-team":
        return "bg-cyan-100 text-cyan-800 border-cyan-200";

      case "tour-operator-partner":
        return "bg-teal-100 text-teal-800 border-teal-200";

      case "tour-operator-pre-approval":
        return "bg-rose-100 text-rose-800 border-rose-200";

      case "tour-operator-manager":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";

      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const fetchItsDetails = async (itsNo: string) => {
    if (!itsNo || itsNo.length !== 8) {
      setIsItsVerified(false);
      setItsNotFound(false);
      return;
    }

    // Never lookup the special Non-ITS number
    if (itsNo === "99999999") {
      return;
    }

    try {
      setIsFetchingIts(true);
      setItsNotFound(false);

      const info = await getDetailsByITS(itsNo, !editingUser);

      if (!info) {
        // ITS number is valid format but no record exists
        setFormName("");
        setFormEmail("");
        setFormPhone("");
        setFormWhatsapp("");

        setFormIsNonItsUser(false);
        setIsItsVerified(false);
        setItsNotFound(true);

        setErrors((prev) => ({
          ...prev,
          formItsNumber: "No record found for this ITS number.",
        }));

        return;
      }

      // ITS record found
      setFormName(info.fullName || "");
      setFormEmail(info.email || "");
      setFormPhone(info.mobile_No || "");
      setFormWhatsapp(info.whatsApp_No || "");

      setFormIsNonItsUser(false);
      setIsItsVerified(true);
      setItsNotFound(false);

      setErrors((prev) => ({
        ...prev,
        formItsNumber: "",
        formName: "",
        formEmail: "",
        formPhone: "",
        formWhatsapp: "",
      }));
    } catch (error) {
      console.error("Failed to fetch ITS details:", error);

      setFormName("");
      setFormEmail("");
      setFormPhone("");
      setFormWhatsapp("");

      setFormIsNonItsUser(false);
      setIsItsVerified(false);
      setItsNotFound(true);

      setErrors((prev) => ({
        ...prev,
        formItsNumber: "No record found for this ITS number.",
      }));
    } finally {
      setIsFetchingIts(false);
    }
  };

  const generateStrongPassword = () => {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const digits = "0123456789";
    const special = "!@#$%^&*()_+";

    const all = upper + lower + digits + special;

    let password =
      upper[Math.floor(Math.random() * upper.length)] +
      lower[Math.floor(Math.random() * lower.length)] +
      digits[Math.floor(Math.random() * digits.length)] +
      special[Math.floor(Math.random() * special.length)];

    for (let i = 0; i < 6; i++) {
      password += all[Math.floor(Math.random() * all.length)];
    }

    return password;
  };

  const handleSaveUser = async () => {
    if (!validateForm()) return;

    setSavingUser(true);

    try {
      if (!editingUser) {
        const createPayload: CreateUserPayload = {
          userCode: generateUserCode(),
          fullName: formName.trim(),
          email: formEmail.trim(),
          plainPassword: generateStrongPassword(),
          mobileNo: formPhone?.trim() || "",
          whatsAppNumber: formWhatsapp?.trim() || "",
          itsNo: formItsNumber,
          status: formIsActive ? "Active" : "Inactive",
          validityStartDate: new Date().toISOString(),
          validityEndDate: formExpiryDate
            ? new Date(formExpiryDate).toISOString()
            : null,
          canRequestUser: formCanRequestUser,
          venueId: formVenue ? Number(formVenue) : null,
          miqaatId: null,
          roleIds: formRoles,
          createdBy: 1,
          isNonItsUser: formIsNonItsUser || !isItsVerified,
        };

        await createUser(createPayload);

        const summary = await getDashboardSummary();
        setDashboardSummary(summary);
      } else {
        const updatePayload = {
          userCode: editingUser.userCode,
          fullName: formName.trim(),
          email: formEmail.trim(),
          plainPassword: "",
          mobileNo: formPhone?.trim() || "",
          whatsAppNumber: formWhatsapp?.trim() || "",
          itsNo: formItsNumber,
          status: formIsActive ? "Active" : "Inactive",
          validityStartDate: editingUser.createdDate
            ? new Date(editingUser.createdDate).toISOString()
            : new Date().toISOString(),
          validityEndDate: formExpiryDate
            ? new Date(formExpiryDate).toISOString()
            : null,
          canRequestUser: formCanRequestUser,
          venueId: formVenue ? Number(formVenue) : null,
          miqaatId: null,
          roleIds: formRoles,
          isNonItsUser: formIsNonItsUser || !isItsVerified,
        };

        await updateUser(Number(editingUser.id), updatePayload);

        const summary = await getDashboardSummary();
        setDashboardSummary(summary);
      }

      const res = await getUsers(1, 1000);
      const baseUsers = res.users ?? [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const usersWithRoles: SystemUser[] = baseUsers.map((u: any) => ({
        id: String(u.userId),
        userCode: u.userCode,
        name: u.fullName,
        email: u.email,
        mobileNo: u.mobileNo,
        officeName: u.officeName,
        whatsAppNumber: u.whatsAppNumber,

        roleIds: u.roleIdsArray ?? [],

        backendRoles:
          u.roleIdsArray?.map((id: number, index: number) => ({
            roleId: id,
            roleName: u.roleNamesArray?.[index],
          })) ?? [],

        isActive: u.status === "Active",
        createdDate: u.validityStartDate?.split("T")[0],
        expiryDate: u.validityEndDate?.split("T")[0],
        itsNumber: u.itsNo,
      }));

      setUsers(usersWithRoles);
      setIsDialogOpen(false);

      toast.success(
        editingUser ? "User updated successfully" : "User created successfully",
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error("Save user error:", error.response?.data || error);

      toast.error(error.response?.data?.message || "Failed to save user");
    } finally {
      setSavingUser(false);
    }
  };

  const handleSaveTempUser = () => {
    if (!editingTempUser) return;

    if (!formName || !formEmail) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formRoles.length === 0) {
      toast.error("Please select at least one role");
      return;
    }

    setTemporaryUsers(
      temporaryUsers.map((tu) =>
        tu.id === editingTempUser.id
          ? {
              ...tu,
              name: formName,
              email: formEmail,
              itsNumber: formItsNumber,
              phone: formPhone,
              venue: formVenue,
              package: formPackage,
              requestedRole:
                rolesFromApi.find((r) => r.roleId === formRoles[0])?.roleName ||
                "",
              roles: formRoles.map(String),

              expiryDate: formExpiryDate,
              remark: formRemark,
            }
          : tu,
      ),
    );

    toast.success("Temporary user request updated successfully");
    setIsDialogOpen(false);
  };

  const handleApproveUser = (tempUser: TemporaryUser) => {
    const newUser: SystemUser = {
      id: `user-${Date.now()}`,
      name: tempUser.name,
      email: tempUser.email,
      userCode: tempUser.userCode,
      roleIds: tempUser.roles?.map(Number) || [],
      backendRoles: rolesFromApi
        .filter((r) => tempUser.roles?.includes(String(r.roleId)))
        .map((r) => ({
          roleId: r.roleId,
          roleName: r.roleName,
        })),
      department: "",
      isActive: true,
      createdDate: new Date().toISOString().split("T")[0],
      expiryDate: tempUser.expiryDate,
      itsNumber: tempUser.itsNumber,
      venue: tempUser.venue,
      package: tempUser.package,
    };

    setUsers([...users, newUser]);
    setTemporaryUsers(temporaryUsers.filter((tu) => tu.id !== tempUser.id));
    toast.success(`User "${tempUser.name}" approved successfully`);
  };

  // const handleRejectUser = (tempUser: TemporaryUser) => {
  //   const reason = window.prompt('Please enter rejection reason:');
  //   if (reason) {
  //     const rejectedUser: RejectedUser = {
  //       id: tempUser.id,
  //       itsNumber: tempUser.itsNumber,
  //       name: tempUser.name,
  //       email: tempUser.email,
  //       phone: tempUser.phone,
  //       venue: tempUser.venue,
  //       package: tempUser.package,
  //       requestedRole: tempUser.requestedRole,
  //       remark: tempUser.remark,
  //       requestedDate: tempUser.requestedDate,
  //       rejectedDate: new Date().toISOString().split('T')[0],
  //       rejectionReason: reason,
  //       rejectedBy: 'Current Admin',
  //       requestedBy: ''
  //     };
  //     setRejectedUsers([...rejectedUsers, rejectedUser]);
  //     setTemporaryUsers(temporaryUsers.filter(tu => tu.id !== tempUser.id));
  //     toast.success(`Request from "${tempUser.name}" rejected`);
  //   }
  // };

  // const handleDeleteUser = (userId: string) => {
  //   if (window.confirm('Are you sure you want to delete this user?')) {
  //     setUsers(users.filter(u => u.id !== userId));
  //     toast.success('User deleted successfully');
  //   }
  // };

  const handleToggleStatus = (user: SystemUser) => {
    setActiveUserToToggle(user);
    setIsStatusDialogOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!activeUserToToggle) return;

    try {
      setSavingUser(true);
      const newStatus = !activeUserToToggle.isActive;
      await toggleUserStatus(Number(activeUserToToggle.id), newStatus);
      await loadUsers();
      toast.success(
        newStatus
          ? `User "${activeUserToToggle.name}" activated successfully`
          : `User "${activeUserToToggle.name}" deactivated successfully`,
      );
      setIsStatusDialogOpen(false);
      setActiveUserToToggle(null);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error(
        "Failed to update user status:",
        error.response?.data || error,
      );
      toast.error(
        error.response?.data?.message || "Failed to update user status",
      );
    } finally {
      setSavingUser(false);
    }
  };

  const activeUsers = users.filter((u) => u.isActive).length;
  const pendingRequests = temporaryUsers.filter(
    (u) => u.status === "pending",
  ).length;
  const approvedTempUsers = temporaryUsers.filter(
    (u) => u.status === "approved",
  ).length;

  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-4">
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search users..."
                value={filters.search}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    search: e.target.value,
                  });
                  setCurrentPage(1);
                }}
                className="h-10 pl-9 pr-9 shadow-sm"
              />

              {filters.search && (
                <button
                  type="button"
                  onClick={() => {
                    setFilters({
                      ...filters,
                      search: "",
                    });
                    setCurrentPage(1);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowStatistics(!showStatistics)}
              className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10 shrink-0"
            >
              {showStatistics ? (
                <ArrowUp className="w-5 h-5" />
              ) : (
                <BarChart3 className="w-5 h-5" />
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`shrink-0 ${
                showFilters
                  ? "text-white"
                  : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
              }`}
              title="Advanced Filters"
            >
              <Filter className="w-4 h-4 sm:mr-0" />
            </Button>
            {canAdd && (
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleOpenDialog();
                }}
                style={{ background: "#5B9BD5" }}
                className="text-white shrink-0"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add New User
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        {showStatistics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">Total Users</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {dashboardSummary?.totalUsers ?? 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Active Users</p>
                    <p className="text-2xl font-bold text-green-900">
                      {dashboardSummary?.activeUsers ?? 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <UserCheck className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-orange-200 bg-orange-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-600">Temporary Users</p>
                    <p className="text-2xl font-bold text-orange-900">
                      {pendingRequests + approvedTempUsers}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600">Rejected Users</p>
                    <p className="text-2xl font-bold text-red-900">
                      {rejectedUsers.length}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Advanced Filters Section */}
        {showFilters && (
          <Card
            className="border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50"
            style={{ gap: "0px", padding: "20px" }}
          >
            <CardHeader style={{ padding: "0px" }}>
              <div
                className="flex items-center justify-between"
                style={{ gap: "0px" }}
              >
                <div>
                  {/* <CardTitle
                    style={{ color: "#5B9BD5" }}
                    className="flex items-center gap-1.5 sm:gap-2 text-base sm:text-lg"
                  >
                    <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
                    Advanced Filters
                  </CardTitle> */}
                  <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                    Filter users by multiple criteria
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(false)}
                  className="text-gray-500 hover:text-gray-700 h-7 w-7 sm:h-8 sm:w-8 p-0"
                >
                  <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent style={{ padding: "0px" }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {/* Name Filter */}
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="filter-name" className="text-xs sm:text-sm">
                    Name
                  </Label>
                  <Input
                    id="filter-name"
                    placeholder="Search by name..."
                    value={filters.name}
                    onChange={(e) =>
                      setFilters({ ...filters, name: e.target.value })
                    }
                    className="h-8 sm:h-9 text-xs sm:text-sm"
                  />
                </div>

                {/* Email Filter */}
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="filter-email" className="text-xs sm:text-sm">
                    Email
                  </Label>
                  <Input
                    id="filter-email"
                    placeholder="Search by email..."
                    value={filters.email}
                    onChange={(e) =>
                      setFilters({ ...filters, email: e.target.value })
                    }
                    className="h-8 sm:h-9 text-xs sm:text-sm"
                  />
                </div>

                {/* ITS Number Filter */}
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="filter-its" className="text-xs sm:text-sm">
                    ITS Number
                  </Label>
                  <Input
                    id="filter-its"
                    placeholder="Search by ITS..."
                    value={filters.itsNumber}
                    onChange={(e) =>
                      setFilters({ ...filters, itsNumber: e.target.value })
                    }
                    className="h-8 sm:h-9 text-xs sm:text-sm"
                  />
                </div>

                {/* Role Filter */}
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="filter-role" className="text-xs sm:text-sm">
                    Role
                  </Label>
                  <Select
                    value={filters.role}
                    onValueChange={(value) =>
                      setFilters({ ...filters, role: value })
                    }
                  >
                    <SelectTrigger
                      id="filter-role"
                      className="h-8 sm:h-9 text-xs sm:text-sm"
                    >
                      <SelectValue placeholder="All roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {rolesFromApi.map((role) => (
                        <SelectItem
                          key={role.roleId}
                          value={role.roleId.toString()}
                        >
                          {role.roleName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="filter-status" className="text-xs sm:text-sm">
                    Status
                  </Label>
                  <Select
                    value={filters.status}
                    onValueChange={(value) =>
                      setFilters({ ...filters, status: value })
                    }
                  >
                    <SelectTrigger
                      id="filter-status"
                      className="h-8 sm:h-9 text-xs sm:text-sm"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="In-Active">Inactive Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Active Filters Summary & Clear Button */}
              {(filters.name ||
                filters.email ||
                filters.itsNumber ||
                filters.role !== "all" ||
                filters.status !== "all") && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 p-2.5 sm:p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                  <div className="flex-1">
                    <p className="text-xs sm:text-sm font-medium text-[#5B9BD5] mb-1.5 sm:mb-2">
                      Active Filters:
                    </p>
                    <div className="flex flex-wrap gap-1 sm:gap-1.5">
                      {filters.name && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5"
                        >
                          Name: {filters.name}
                        </Badge>
                      )}
                      {filters.email && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5"
                        >
                          Email: {filters.email}
                        </Badge>
                      )}
                      {filters.itsNumber && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5"
                        >
                          ITS: {filters.itsNumber}
                        </Badge>
                      )}
                      {filters.role !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5"
                        >
                          Role:{" "}
                          {
                            rolesFromApi.find(
                              (r) => r.roleId === Number(filters.role),
                            )?.roleName
                          }
                        </Badge>
                      )}
                      {filters.status !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5"
                        >
                          Status: {filters.status}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFilters({
                        search: "",
                        name: "",
                        email: "",
                        role: "all",
                        status: "all",
                        itsNumber: "",
                      })
                    }
                    className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10 h-7 sm:h-8 text-xs sm:text-sm w-full sm:w-auto"
                  >
                    Clear All
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Main Content Card */}
        <Card
          className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg"
          style={{ gap: "0px", marginTop: "10px" }}
        >
          <CardContent style={{ padding: "0px" }}>
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
              style={{ gap: "0px" }}
            >
              <div
                className="sm:px-6 sm:pt-6 overflow-x-auto"
                style={{ padding: "20px" }}
              >
                <TabsList className="w-full justify-start bg-gray-100/80 h-auto p-0.5 sm:p-1 space-x-0 rounded-full inline-flex">
                  <TabsTrigger
                    value="active-users"
                    className="rounded-full px-3 sm:px-6 py-1.5 sm:py-2 data-[state=active]:bg-[#5B9BD5] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 transition-all text-xs sm:text-sm whitespace-nowrap"
                  >
                    Active Users
                  </TabsTrigger>
                  <TabsTrigger
                    value="temporary-users"
                    className="rounded-full px-3 sm:px-6 py-1.5 sm:py-2 data-[state=active]:bg-[#5B9BD5] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 transition-all text-xs sm:text-sm whitespace-nowrap"
                  >
                    <span className="hidden sm:inline">Temporary Users</span>
                    <span className="sm:hidden">Temporary</span> (
                    {temporaryUsers.length})
                  </TabsTrigger>
                  <TabsTrigger
                    value="rejected-users"
                    className="rounded-full px-3 sm:px-6 py-1.5 sm:py-2 data-[state=active]:bg-[#5B9BD5] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 transition-all text-xs sm:text-sm whitespace-nowrap"
                  >
                    <span className="hidden sm:inline">Rejected Users</span>
                    <span className="sm:hidden">Rejected</span> (
                    {rejectedUsers.length})
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Active Users Tab */}
              <TabsContent
                value="active-users"
                className="p-2 sm:p-6 space-y-4 m-0"
                style={{ padding: "0px" }}
              >
                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {paginatedUsers.map((user) => (
                    <Card
                      key={user.id}
                      className="border-[#5B9BD5]/20 hover:border-[#5B9BD5]/40 transition-colors"
                    >
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 text-sm truncate">
                              {user.name}
                            </h4>
                            {user.backendRoles?.some(
                              (r) =>
                                r.roleName.toLowerCase() === "tour operator",
                            ) &&
                              user.venue && (
                                <p className="text-[10px] text-gray-500 truncate mt-0.5">
                                  {user.venue}
                                </p>
                              )}

                            <p className="text-xs text-gray-600 truncate mt-0.5">
                              {user.email}
                            </p>
                            {user.mobileNo && (
                              <p className="text-[11px] text-gray-500">
                                {user.mobileNo}
                              </p>
                            )}
                            {user.itsNumber && (
                              <p className="text-[10px] text-[#5B9BD5] font-medium mt-0.5">
                                ITS: {user.itsNumber}
                              </p>
                            )}
                            {user.officeName && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-teal-100 text-teal-800 text-[11px] border border-teal-200">
                                {user.officeName}
                              </span>
                            )}
                          </div>
                          <Switch
                            checked={user.isActive}
                            onCheckedChange={() => handleToggleStatus(user)}
                            className="data-[state=checked]:bg-[#5B9BD5] flex-shrink-0"
                          />
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] text-gray-500 mb-1">
                              Role
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {user.backendRoles &&
                              user.backendRoles.length > 0 ? (
                                user.backendRoles.map((r) => (
                                  <Badge
                                    key={r.roleId}
                                    variant="outline"
                                    className={`${getRoleColor(r.roleName)} text-[10px]`}
                                  >
                                    {r.roleName}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-gray-500 text-xs">-</span>
                              )}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] text-gray-500 mb-1">
                              Expiry Date
                            </p>
                            <p className="text-xs text-gray-700">
                              {user.expiryDate ? (
                                new Date(user.expiryDate).toLocaleDateString()
                              ) : (
                                <span className="text-gray-500">Permanent</span>
                              )}
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-2 pt-2 border-t border-gray-100">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenDialog(user)}
                            className="flex-1 h-8 text-xs border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10"
                          >
                            <Edit className="w-3.5 h-3.5 mr-1.5" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setActiveUserToDelete(user);
                              setRejectionRemark("");
                              setIsRejectionDialogOpen(true);
                            }}
                            className="flex-1 h-8 text-xs border-red-200 text-red-600 hover:bg-red-50"
                            disabled={user.backendRoles?.some(
                              (r) => r.roleName.toLowerCase() === "super admin",
                            )}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                            Delete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {filteredUsers.length === 0 && (
                    <Card className="border-[#5B9BD5]/20">
                      <CardContent className="p-8 text-center">
                        <p className="text-sm text-gray-500">No users found</p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Desktop Table View */}
                <div
                  className="hidden md:block overflow-x-auto"
                  style={{ marginTop: "0px" }}
                >
                  <Table>
                    <TableHeader>
                      <TableRow
                        style={{ backgroundColor: "#E3F2FD" }}
                        className="hover:bg-[#E3F2FD]"
                      >
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="cursor-pointer"
                          onClick={() => handleSort("name")}
                        >
                          <div className="flex items-center gap-1">
                            Name
                            {sortColumn === "name" ? (
                              sortDirection === "asc" ? (
                                <ArrowUp className="w-3 h-3" />
                              ) : (
                                <ArrowDown className="w-3 h-3" />
                              )
                            ) : (
                              <ArrowUpDown className="w-3 h-3 opacity-50" />
                            )}
                          </div>
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="cursor-pointer"
                          onClick={() => handleSort("email")}
                        >
                          <div className="flex items-center gap-1">
                            Email
                            {sortColumn === "email" ? (
                              sortDirection === "asc" ? (
                                <ArrowUp className="w-3 h-3" />
                              ) : (
                                <ArrowDown className="w-3 h-3" />
                              )
                            ) : (
                              <ArrowUpDown className="w-3 h-3 opacity-50" />
                            )}
                          </div>
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="cursor-pointer"
                          onClick={() => handleSort("role")}
                        >
                          <div className="flex items-center gap-1">
                            Role
                            {sortColumn === "role" ? (
                              sortDirection === "asc" ? (
                                <ArrowUp className="w-3 h-3" />
                              ) : (
                                <ArrowDown className="w-3 h-3" />
                              )
                            ) : (
                              <ArrowUpDown className="w-3 h-3 opacity-50" />
                            )}
                          </div>
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs font-semibold"
                        >
                          Expiry Date
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs font-semibold"
                        >
                          Status
                        </TableHead>
                        {(canEdit || canDelete) && (
                          <TableHead
                            style={{ color: "#5B9BD5" }}
                            className="px-4 py-3 text-xs text-right"
                          >
                            Actions
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedUsers.map((user) => (
                        <TableRow key={user.id} className="hover:bg-sky-50/30">
                          <TableCell className="px-4">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">
                                {user.name}
                              </p>
                              {user.backendRoles?.some(
                                (r) =>
                                  r.roleName.toLowerCase() === "tour operator",
                              ) &&
                                user.venue && (
                                  <p className="text-[10px] text-gray-500">
                                    {user.venue}
                                  </p>
                                )}
                              <div className="flex items-center gap-2 flex-wrap mt-1">
                                {user.itsNumber && (
                                  <p className="text-[10px] text-[#5B9BD5] font-medium">
                                    {" "}
                                    ITS: {user.itsNumber}{" "}
                                  </p>
                                )}

                                {user.officeName && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-teal-100 text-teal-800 text-[11px] border border-teal-200">
                                    {user.officeName}
                                  </span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="px-4 text-gray-700 text-sm">
                            <div>
                              <p>{user.email}</p>

                              {user.mobileNo && (
                                <p className="text-[11px] text-gray-500">
                                  {user.mobileNo}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="px-4">
                            <div className="flex flex-wrap gap-2">
                              {user.backendRoles &&
                              user.backendRoles.length > 0 ? (
                                user.backendRoles.map((r) => (
                                  <Badge
                                    key={r.roleId}
                                    variant="outline"
                                    className={`${getRoleColor(r.roleName)} text-xs`}
                                  >
                                    {r.roleName}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-gray-500 text-sm">-</span>
                              )}
                            </div>
                          </TableCell>

                          <TableCell className="px-4 text-gray-700 text-sm">
                            {user.expiryDate ? (
                              <span className="text-xs">
                                {new Date(user.expiryDate).toLocaleDateString()}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-500">
                                Permanent
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="px-4">
                            <Switch
                              checked={user.isActive}
                              onCheckedChange={() => handleToggleStatus(user)}
                              className="data-[state=checked]:bg-[#5B9BD5]"
                            />
                          </TableCell>
                          <TableCell className="px-4">
                            <div className="flex gap-2 justify-end">
                              {canEdit && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleOpenDialog(user)}
                                  className="h-8 w-8 p-0 hover:bg-gray-100"
                                >
                                  <Edit className="w-4 h-4 text-gray-600" />
                                </Button>
                              )}
                              {canDelete && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setActiveUserToDelete(user);
                                    setRejectionRemark("");
                                    setIsRejectionDialogOpen(true);
                                  }}
                                  className="h-8 w-8 p-0 hover:bg-red-50"
                                  disabled={
                                    !user.isActive ||
                                    user.backendRoles?.some(
                                      (r) =>
                                        r.roleName.toLowerCase() ===
                                        "super admin",
                                    )
                                  }
                                  title={
                                    user.backendRoles?.some(
                                      (r) =>
                                        r.roleName.toLowerCase() ===
                                        "super admin",
                                    )
                                      ? "Super Admin cannot be deleted"
                                      : !user.isActive
                                        ? "Inactive users cannot be deleted"
                                        : "Delete User"
                                  }
                                >
                                  <Trash2
                                    className={`w-4 h-4 ${
                                      !user.isActive ||
                                      user.backendRoles?.some(
                                        (r) =>
                                          r.roleName.toLowerCase() ===
                                          "super admin",
                                      )
                                        ? "text-red-400"
                                        : "text-red-600"
                                    }`}
                                  />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredUsers.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="text-center py-8 px-4 text-gray-500 text-sm"
                          >
                            No users found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                <Pagination
                  totalItems={filteredUsers.length}
                  itemsPerPage={pageSize}
                  currentPage={currentPage}
                  onPageChange={(page) => setCurrentPage(page)}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                  }}
                  label="users"
                />
              </TabsContent>

              {/* Temporary Users Tab */}
              <TabsContent
                value="temporary-users"
                className="p-3 sm:p-6 space-y-4 m-0"
              >
                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {filteredTempUsers.map((tempUser) => (
                    <Card
                      key={tempUser.id}
                      className="border-[#5B9BD5]/20 hover:border-[#5B9BD5]/40 transition-colors"
                    >
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 text-sm truncate">
                              {tempUser.name}
                            </h4>
                            <p className="text-xs text-gray-600 truncate mt-0.5">
                              {tempUser.email}
                            </p>
                            {tempUser.itsNumber && (
                              <p className="text-[10px] text-gray-500 mt-0.5">
                                {tempUser.itsNumber}
                              </p>
                            )}
                          </div>
                          {tempUser.status === "pending" && (
                            <Badge
                              variant="outline"
                              className="bg-yellow-50 text-yellow-700 border-yellow-200 text-[10px] px-2 py-0.5 flex-shrink-0"
                            >
                              Pending
                            </Badge>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-[10px] text-gray-500 mb-1">
                              Requester
                            </p>
                            <p className="text-xs text-gray-700 truncate">
                              {tempUser.requestedBy || "-"}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] text-gray-500 mb-1">
                              Expiry Date
                            </p>
                            <p className="text-xs text-gray-700">
                              {tempUser.expiryDate ? (
                                new Date(
                                  tempUser.expiryDate,
                                ).toLocaleDateString()
                              ) : (
                                <span className="text-gray-500">-</span>
                              )}
                            </p>
                          </div>
                        </div>

                        <div>
                          <p className="text-[10px] text-gray-500 mb-1">
                            Requested Role
                          </p>
                          <Badge
                            variant="outline"
                            className="bg-blue-50 text-blue-700 border-blue-200 text-xs"
                          >
                            {tempUser.requestedRole}
                          </Badge>
                        </div>

                        <div className="flex gap-2 pt-2 border-t border-gray-100">
                          {tempUser.status === "pending" && (
                            <Button
                              size="sm"
                              onClick={() => handleApproveUser(tempUser)}
                              className="flex-1 h-8 text-xs text-white"
                              style={{ backgroundColor: "#5B9BD5" }}
                            >
                              <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                              Approve
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenTempUserDialog(tempUser)}
                            className="flex-1 h-8 text-xs border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10"
                          >
                            <Edit className="w-3.5 h-3.5 mr-1.5" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setTempUserToReject(tempUser);
                              setRejectionRemark("");
                              setIsRejectionDialogOpen(true);
                            }}
                            className="flex-1 h-8 text-xs border-red-200 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                            Reject
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {filteredTempUsers.length === 0 && (
                    <Card className="border-[#5B9BD5]/20">
                      <CardContent className="p-8 text-center">
                        <p className="text-sm text-gray-500">
                          No temporary users found
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Desktop Table View */}
                <div
                  className="hidden md:block overflow-x-auto"
                  style={{ marginTop: "0px" }}
                >
                  <Table>
                    <TableHeader>
                      <TableRow
                        style={{ backgroundColor: "#E3F2FD" }}
                        className="hover:bg-[#E3F2FD]"
                      >
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs font-semibold"
                        >
                          Name
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs font-semibold"
                        >
                          Email
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs font-semibold"
                        >
                          Requester Name
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs font-semibold"
                        >
                          Expiry Date
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs font-semibold"
                        >
                          Roles
                        </TableHead>
                        {canEdit && (
                          <TableHead
                            style={{ color: "#5B9BD5" }}
                            className="px-4 py-3 text-xs font-semibold text-right"
                          >
                            Actions
                          </TableHead>
                        )}
                        {canDelete && (
                          <TableHead
                            style={{ color: "#5B9BD5" }}
                            className="px-4 py-3 text-xs font-semibold text-right"
                          >
                            Actions
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTempUsers.map((tempUser) => (
                        <TableRow
                          key={tempUser.id}
                          className="hover:bg-sky-50/30"
                        >
                          <TableCell className="px-4 py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">
                                {tempUser.name}
                              </p>
                              {tempUser.itsNumber && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {tempUser.itsNumber}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-3 text-gray-700 text-sm">
                            {tempUser.email}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-gray-700 text-sm">
                            {tempUser.requestedBy || "-"}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-gray-700 text-xs">
                            {tempUser.expiryDate ? (
                              <span>
                                {new Date(
                                  tempUser.expiryDate,
                                ).toLocaleDateString()}
                              </span>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </TableCell>
                          <TableCell className="px-4 py-3">
                            <Badge
                              variant="outline"
                              className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] px-1.5 py-0.5"
                            >
                              {tempUser.requestedRole}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-4 py-3">
                            <div className="flex gap-2 justify-end">
                              {canEdit && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    handleOpenTempUserDialog(tempUser)
                                  }
                                  className="h-8 w-8 p-0 hover:bg-gray-100"
                                  title="Edit Request"
                                >
                                  <Edit className="w-4 h-4 text-gray-600" />
                                </Button>
                              )}
                              {canDelete && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setTempUserToReject(tempUser);
                                    setRejectionRemark("");
                                    setIsRejectionDialogOpen(true);
                                  }}
                                  className="h-8 w-8 p-0 hover:bg-red-50"
                                  title="Delete/Reject"
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              )}
                              {tempUser.status === "pending" && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleApproveUser(tempUser)}
                                    className="h-8 w-8 p-0 hover:bg-green-50"
                                    title="Approve"
                                  >
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredTempUsers.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="text-center py-8 px-4 text-gray-500 text-sm"
                          >
                            No temporary user requests found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              {/* Rejected Users Tab */}
              <TabsContent
                value="rejected-users"
                className="p-3 sm:p-6 space-y-4 m-0"
              >
                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {filteredRejectedUsers.map((rejectedUser) => (
                    <Card
                      key={rejectedUser.id}
                      className="border-red-200 bg-red-50/30"
                    >
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 text-sm truncate">
                              {rejectedUser.name}
                            </h4>
                            <p className="text-xs text-gray-600 truncate mt-0.5">
                              {rejectedUser.email}
                            </p>
                            {rejectedUser.itsNumber && (
                              <p className="text-[10px] text-gray-500 mt-0.5">
                                {rejectedUser.itsNumber}
                              </p>
                            )}
                          </div>
                          <Badge
                            variant="outline"
                            className="bg-red-100 text-red-700 border-red-300 text-[10px] px-2 py-0.5 flex-shrink-0"
                          >
                            Rejected
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-[10px] text-gray-500 mb-1">
                              Requester
                            </p>
                            <p className="text-xs text-gray-700 truncate">
                              {rejectedUser.requestedBy || "-"}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] text-gray-500 mb-1">
                              Requested Date
                            </p>
                            <p className="text-xs text-gray-700">
                              {new Date(
                                rejectedUser.requestedDate,
                              ).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-[10px] text-gray-500 mb-1">
                              Rejected By
                            </p>
                            <p className="text-xs text-gray-700 truncate">
                              {rejectedUser.rejectedBy || "-"}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] text-gray-500 mb-1">
                              Rejected Date
                            </p>
                            <p className="text-xs text-gray-700">
                              {new Date(
                                rejectedUser.rejectedDate,
                              ).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        <div>
                          <p className="text-[10px] text-gray-500 mb-1">
                            Rejection Reason
                          </p>
                          <p className="text-xs text-gray-700 leading-relaxed">
                            {rejectedUser.rejectionReason}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {filteredRejectedUsers.length === 0 && (
                    <Card className="border-[#5B9BD5]/20">
                      <CardContent className="p-8 text-center">
                        <p className="text-sm text-gray-500">
                          No rejected user requests found
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Desktop Table View */}
                <div
                  className="hidden md:block overflow-x-auto"
                  style={{ marginTop: "0px" }}
                >
                  <Table>
                    <TableHeader>
                      <TableRow
                        style={{ backgroundColor: "#E3F2FD" }}
                        className="hover:bg-[#E3F2FD]"
                      >
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs font-semibold"
                        >
                          Name
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs font-semibold"
                        >
                          Email
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs font-semibold"
                        >
                          Requester Name
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs font-semibold"
                        >
                          Requested Date
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs font-semibold"
                        >
                          Rejected By
                        </TableHead>
                        <TableHead
                          style={{ color: "#5B9BD5" }}
                          className="px-4 py-3 text-xs font-semibold"
                        >
                          Rejection Reason
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRejectedUsers.map((rejectedUser) => (
                        <TableRow
                          key={rejectedUser.id}
                          className="hover:bg-sky-50/30"
                        >
                          <TableCell className="px-4 py-3">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">
                                {rejectedUser.name}
                              </p>
                              {rejectedUser.itsNumber && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {rejectedUser.itsNumber}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-3 text-gray-700 text-sm">
                            {rejectedUser.email}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-gray-700 text-sm">
                            {rejectedUser.requestedBy || "-"}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-gray-700 text-xs">
                            {new Date(
                              rejectedUser.requestedDate,
                            ).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-gray-700 text-sm">
                            {rejectedUser.rejectedBy || "-"}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-gray-700 text-xs max-w-xs">
                            <p className="line-clamp-2">
                              {rejectedUser.rejectionReason}
                            </p>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredRejectedUsers.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="text-center py-8 px-4 text-gray-500 text-sm"
                          >
                            No rejected user requests found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Add/Edit User Dialog */}
        <Dialog
          open={isDialogOpen}
          onOpenChange={(open) => {
            if (!savingUser) {
              setIsDialogOpen(open);
            }
          }}
        >
          <DialogContent
            className="!w-[calc(80vw-1rem)] sm:!w-[60vw] !max-w-[1200px] !max-h-[90vh] overflow-y-auto bg-white"
            style={{ gap: "0px" }}
          >
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                {isEditingTemp
                  ? "Edit Temporary User Request"
                  : editingUser
                    ? "Edit User"
                    : "Add New User"}
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                {isEditingTemp
                  ? "Update temporary user request details and role assignment"
                  : editingUser
                    ? "Update user information and permissions"
                    : "Create a new system user with role assignments"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 mt-4">
              {/* Basic Information */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-800">
                  Basic Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <InputFieldset
                      label="ITS Number"
                      required
                      value={formItsNumber}
                      disabled={!!editingUser || formIsNonItsUser}
                      maxLength={8}
                      inputMode="numeric"
                      pattern="\d*"
                      placeholder="Enter 8-digit ITS number"
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 8);

                        setFormItsNumber(value);

                        setIsItsVerified(false);
                        setItsNotFound(false);

                        setErrors((prev) => ({
                          ...prev,
                          formItsNumber: "",
                        }));

                        setHasFetchedIts(false);
                      }}
                      className={`border-gray-300 focus:border-[#5B9BD5] focus:ring-[#5B9BD5]
                        ${editingUser ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}
                        ${errors.formItsNumber ? "border-red-500" : ""}`}
                    />

                    {errors.formItsNumber && (
                      <p className="text-xs text-red-600 mt-1">
                        {errors.formItsNumber}
                      </p>
                    )}
                    {isFetchingIts && (
                      <p className="text-sky-600 text-sm mt-1 flex items-center gap-1">
                        <Search className="h-3 w-3 animate-pulse" />
                        Fetching user details...
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <CheckboxFieldset label="User Type">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={formIsNonItsUser}
                          disabled={!!editingUser}
                          onCheckedChange={(checked) => {
                            const isChecked = Boolean(checked);

                            if (isChecked) {
                              // Enable Non-ITS mode
                              setFormIsNonItsUser(true);
                              setIsItsVerified(false);
                              setItsNotFound(false);

                              // Fixed ITS number for Non-ITS users
                              setFormItsNumber("99999999");

                              // Clear any previous ITS validation error
                              setErrors((prev) => ({
                                ...prev,
                                formItsNumber: "",
                                formName: "",
                                formEmail: "",
                                formPhone: "",
                                formWhatsapp: "",
                              }));
                            } else {
                              // Disable Non-ITS mode
                              setFormIsNonItsUser(false);
                              setIsItsVerified(false);
                              setItsNotFound(false);

                              // Clear ITS number
                              setFormItsNumber("");

                              // Clear ALL previously entered user details
                              setFormName("");
                              setFormEmail("");
                              setFormPhone("");
                              setFormWhatsapp("");
                              setFormVenue("");
                              setFormPackage("");
                              setFormExpiryDate("");
                              setFormRoles([]);
                              setFormDepartment("");
                              setFormEvent("");
                              setFormRemark("");

                              setErrors({});
                            }
                          }}
                        />

                        <label
                          // htmlFor={`non-its-${applicant.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Non ITS User
                        </label>
                      </div>
                    </CheckboxFieldset>
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="Full Name"
                      required
                      value={formName}
                      readOnly={hasFetchedIts}
                      placeholder="Enter full name"
                      disabled={!formIsNonItsUser || isItsVerified}
                      onChange={(e) => {
                        setFormName(e.target.value.replace(/[^A-Za-z\s]/g, ""));
                        setErrors((prev) => ({ ...prev, formName: "" }));
                        if (errors.formName) {
                          setErrors((prev) => ({
                            ...prev,
                            formName: "",
                          }));
                        }
                      }}
                      className={`border-gray-300 focus:border-[#5B9BD5]
                        ${errors.formName ? "border-red-500" : ""}`}
                    />

                    {errors.formName && (
                      <p className="text-xs text-red-600 mt-1">
                        {errors.formName}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="Email Address"
                      required
                      type="email"
                      value={formEmail}
                      readOnly={hasFetchedIts && !isSuperAdmin}
                      placeholder="user@fhims.com"
                      disabled={!formIsNonItsUser && !isSuperAdmin}
                      onChange={(e) => {
                        const value = e.target.value.replace(
                          /[^A-Za-z0-9._@-]/g,
                          "",
                        );

                        setFormEmail(value);

                        setErrors((prev) => ({
                          ...prev,
                          formEmail: "",
                        }));
                      }}
                      className={`border-gray-300 focus:border-[#5B9BD5]
                        ${errors.formEmail ? "border-red-500" : ""}`}
                    />

                    {errors.formEmail && (
                      <p className="text-xs text-red-600 mt-1">
                        {errors.formEmail}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="Mobile Number"
                      required
                      value={formPhone}
                      readOnly={hasFetchedIts && !isSuperAdmin}
                      placeholder="Include country code eg. +91XXXXXXXX01"
                      disabled={!formIsNonItsUser && !isSuperAdmin}
                      onChange={(e) => {
                        const value = e.target.value;

                        if (/^\+?\d{0,15}$/.test(value)) {
                          setFormPhone(value);
                          setErrors((prev) => ({
                            ...prev,
                            formPhone: "",
                          }));
                        }
                      }}
                      maxLength={16}
                      className={`border-gray-300 focus:border-[#5B9BD5]
      ${errors.formPhone ? "border-red-500" : ""}`}
                    />

                    {errors.formPhone && (
                      <p className="text-xs text-red-600 mt-1">
                        {errors.formPhone}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="Whatsapp Number"
                      type="text"
                      value={formWhatsapp}
                      readOnly={hasFetchedIts && !isSuperAdmin}
                      placeholder="Include country code eg. +91XXXXXXXX01"
                      disabled={!formIsNonItsUser && !isSuperAdmin}
                      onChange={(e) => {
                        const value = e.target.value;

                        if (/^\+?\d{0,15}$/.test(value)) {
                          setFormWhatsapp(value);
                          setErrors((prev) => ({
                            ...prev,
                            formWhatsapp: "",
                          }));
                        }
                      }}
                      maxLength={16}
                      className={`border-gray-300 focus:border-[#5B9BD5]
                        ${errors.formWhatsapp ? "border-red-500" : ""}`}
                    />

                    {errors.formWhatsapp && (
                      <p className="text-xs text-red-600 mt-1">
                        {errors.formWhatsapp}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Venue & Miqaat */}
              <div className="space-y-2" style={{ marginTop: "12px" }}>
                <h3 className="text-sm font-semibold text-gray-800">
                  Venue & Miqaat
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <SelectFieldset label="Venue">
                      <Select value={formVenue} onValueChange={setFormVenue}>
                        <SelectTriggerUI className="border-gray-300">
                          <SelectValue placeholder="Select Venue" />
                        </SelectTriggerUI>
                        <SelectContent>
                          {venues
                            .filter((venue) => venue.isActive)
                            .map((venue) => (
                              <SelectItem
                                key={venue.venueId}
                                value={venue.venueId.toString()}
                              >
                                {venue.venueName}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </SelectFieldset>
                  </div>
                </div>
              </div>

              {/* Access Duration */}
              <div className="space-y-3" style={{ marginTop: "12px" }}>
                <h3 className="text-sm font-semibold text-gray-800">
                  Access Duration
                </h3>
                <div className="space-y-2">
                  <InputFieldset
                    label="Expiry Date"
                    type="date"
                    value={formExpiryDate}
                    disabled={!formIsNonItsUser}
                    onChange={(e) => setFormExpiryDate(e.target.value)}
                    placeholder="dd/mm/yyyy"
                    className="border-gray-300 focus:border-[#5B9BD5] focus:ring-[#5B9BD5]"
                  />
                  <p className="text-xs text-gray-500">
                    Leave empty for permanent access
                  </p>
                </div>
              </div>

              {/* Role Assignment */}
              <div className="space-y-3" style={{ marginTop: "12px" }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-800">
                      Role Assignment <span className="text-red-500">*</span>
                    </h3>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsRolesMaximized(true)}
                      className="h-6 w-6 p-0 hover:bg-[#5B9BD5]/10"
                      title="Expand roles view"
                    >
                      <Maximize2 className="w-4 h-4 text-[#5B9BD5]" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Select one or multiple roles
                  </p>
                </div>
                {errors.formRoles && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.formRoles}
                  </p>
                )}

                {/* Generic Roles */}
                {/* Generic Roles */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-3">
                  <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Generic Roles
                  </h4>

                  <div className="grid grid-cols-3 gap-4">
                    {genericRoles.map((role) => (
                      <div
                        key={role.roleId}
                        className="flex items-start space-x-2"
                      >
                        <input
                          type="checkbox"
                          checked={formRoles.includes(role.roleId)}
                          onChange={(e) => {
                            let updatedRoles: number[];

                            if (e.target.checked) {
                              updatedRoles = [...formRoles, role.roleId];
                            } else {
                              updatedRoles = formRoles.filter(
                                (id) => id !== role.roleId,
                              );
                            }

                            setFormRoles(updatedRoles);

                            // Clear validation immediately
                            if (updatedRoles.length > 0) {
                              setErrors((prev) => ({
                                ...prev,
                                formRoles: "",
                              }));
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-[#5B9BD5] focus:ring-[#5B9BD5] cursor-pointer"
                        />

                        <div>
                          <Label className="text-sm text-gray-700 cursor-pointer">
                            {role.roleName}
                          </Label>

                          {/* {role.roleDescription && (
              <p className="text-xs text-gray-500">
                {role.roleDescription}
              </p>
            )} */}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Available for Delegation */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-3">
                  <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Available for Delegation
                  </h4>

                  <div className="grid grid-cols-3 gap-4">
                    {delegationRoles.map((role) => (
                      <div
                        key={role.roleId}
                        className="flex items-start space-x-2"
                      >
                        <input
                          type="checkbox"
                          checked={formRoles.includes(role.roleId)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormRoles([...formRoles, role.roleId]);
                            } else {
                              setFormRoles(
                                formRoles.filter((id) => id !== role.roleId),
                              );
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-[#5B9BD5] focus:ring-[#5B9BD5] cursor-pointer"
                        />

                        <Label className="text-sm text-gray-700 cursor-pointer">
                          {role.roleName}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* User Permissions */}
              <div className="space-y-3" style={{ marginTop: "12px" }}>
                <h3 className="text-sm font-semibold text-gray-800">
                  User Permissions
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={formIsActive}
                        onCheckedChange={setFormIsActive}
                        className="data-[state=checked]:bg-[#5B9BD5]"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Active User
                        </p>
                        <p className="text-xs text-gray-500">
                          User can login and access assigned modules
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                disabled={savingUser}
                className="border-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={isEditingTemp ? handleSaveTempUser : handleSaveUser}
                disabled={savingUser}
                className="text-white"
                style={{ backgroundColor: "#5B9BD5" }}
              >
                {savingUser
                  ? editingUser
                    ? "Updating..."
                    : "Creating..."
                  : editingUser || isEditingTemp
                    ? "Update User"
                    : "Create User"}
              </Button>
            </div>
          </DialogContent>
          {savingUser && (
            <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[#5B9BD5]" />
            </div>
          )}
        </Dialog>

        {/* Rejection Dialog */}
        <Dialog
          open={isRejectionDialogOpen}
          onOpenChange={setIsRejectionDialogOpen}
        >
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                {activeUserToDelete ? "Delete User" : "Reject User Request"}
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Please provide a reason for{" "}
                {activeUserToDelete
                  ? "deleting this user"
                  : "rejecting this user request"}
                .
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <Input
                value={rejectionRemark}
                onChange={(e) => setRejectionRemark(e.target.value)}
                placeholder="Enter rejection reason"
                className="border-gray-300 focus:border-[#5B9BD5] focus:ring-[#5B9BD5]"
              />
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setIsRejectionDialogOpen(false)}
                className="border-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!rejectionRemark.trim()) {
                    toast.error("Please enter a rejection reason");
                    return;
                  }

                  // Handle temporary user rejection
                  if (tempUserToReject) {
                    const rejectedUser: RejectedUser = {
                      id: tempUserToReject.id,
                      itsNumber: tempUserToReject.itsNumber,
                      name: tempUserToReject.name,
                      email: tempUserToReject.email,
                      phone: tempUserToReject.phone,
                      venue: tempUserToReject.venue,
                      package: tempUserToReject.package,
                      requestedRole: tempUserToReject.requestedRole,
                      remark: tempUserToReject.remark,
                      requestedDate: tempUserToReject.requestedDate,
                      rejectedDate: new Date().toISOString().split("T")[0],
                      rejectionReason: rejectionRemark,
                      rejectedBy:
                        tempUserToReject.requestedBy || "Current Admin",
                      requestedBy: "",
                    };
                    setRejectedUsers([...rejectedUsers, rejectedUser]);
                    setTemporaryUsers(
                      temporaryUsers.filter(
                        (tu) => tu.id !== tempUserToReject.id,
                      ),
                    );
                    toast.success(
                      `Request from "${tempUserToReject.name}" rejected`,
                    );
                  }

                  // Handle active user deletion using backend
                  if (activeUserToDelete) {
                    try {
                      const response = await softDeleteUser(
                        Number(activeUserToDelete.id),
                      );

                      if (response?.affected) {
                        await loadUsers();
                        toast.success(
                          `User "${activeUserToDelete.name}" deleted successfully`,
                        );
                      } else {
                        toast.error("Failed to delete user");
                      }
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } catch (error: any) {
                      console.error(
                        "Soft delete error:",
                        error.response?.data || error,
                      );
                      toast.error("Failed to delete user");
                    }
                  }

                  setIsRejectionDialogOpen(false);
                  setRejectionRemark("");
                  setTempUserToReject(null);
                  setActiveUserToDelete(null);
                }}
                className="text-white"
                style={{ backgroundColor: "#5B9BD5" }}
              >
                {activeUserToDelete ? "Delete User" : "Reject User"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Status Change Dialog */}
        <Dialog
          open={isStatusDialogOpen}
          onOpenChange={(open) => {
            if (!savingUser) {
              setIsStatusDialogOpen(open);

              if (!open) {
                setActiveUserToToggle(null);
              }
            }
          }}
        >
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader>
              <DialogTitle
                style={{
                  color: activeUserToToggle?.isActive ? "#DC2626" : "#5B9BD5",
                }}
              >
                {activeUserToToggle?.isActive
                  ? "Deactivate User"
                  : "Activate User"}
              </DialogTitle>

              <DialogDescription className="text-gray-600">
                Are you sure you want to{" "}
                {activeUserToToggle?.isActive ? "deactivate" : "activate"}{" "}
                <strong>"{activeUserToToggle?.name}"</strong>? This action
                cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsStatusDialogOpen(false);
                  setActiveUserToToggle(null);
                }}
                disabled={savingUser}
                className="border-gray-300"
              >
                No, Cancel
              </Button>

              <Button
                onClick={handleConfirmStatusChange}
                disabled={savingUser}
                className="text-white"
                style={{
                  backgroundColor: activeUserToToggle?.isActive
                    ? "#DC2626"
                    : "#5B9BD5",
                }}
              >
                {savingUser
                  ? "Updating..."
                  : activeUserToToggle?.isActive
                    ? "Yes, Deactivate"
                    : "Yes, Activate"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Maximized Roles View Dialog */}
        <Dialog open={isRolesMaximized} onOpenChange={setIsRolesMaximized}>
          <DialogContent className="!w-[calc(100vw-1rem)] sm:!w-[90vw] !max-w-[1600px] !max-h-[90vh] bg-white !p-3 sm:!p-4 md:!p-6 overflow-hidden">
            <DialogHeader className="pb-2 sm:pb-3">
              <DialogTitle
                style={{ color: "#5B9BD5" }}
                className="flex items-center gap-2 text-base sm:text-lg md:text-xl"
              >
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                <span>All Available Roles</span>
              </DialogTitle>
              <DialogDescription className="text-gray-600 text-[10px] sm:text-xs md:text-sm">
                Select one or multiple roles for the user. Click on any card to
                select/deselect.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 sm:space-y-4 md:space-y-6 overflow-y-auto max-h-[calc(90vh-200px)] sm:max-h-[calc(90vh-220px)] pr-1 sm:pr-2">
              <div className="space-y-2 sm:space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-2 sm:pb-3 border-b-2 border-[#5B9BD5]/30">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-lg bg-[#5B9BD5]/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-[#5B9BD5]" />
                    </div>
                    <h3 className="text-xs sm:text-sm md:text-base font-semibold text-gray-800">
                      Generic Roles
                    </h3>
                  </div>

                  <Badge
                    variant="outline"
                    className="border-[#5B9BD5] bg-[#5B9BD5]/10 text-[#5B9BD5] text-[10px] sm:text-xs px-2 sm:px-3 py-0.5 sm:py-1 w-fit"
                  >
                    {
                      formRoles.filter((id) =>
                        genericRoles.some((r) => r.roleId === id),
                      ).length
                    }{" "}
                    of {genericRoles.length} selected
                  </Badge>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-2.5 md:gap-3">
                  {genericRoles.map((role) => {
                    const selected = formRoles.includes(role.roleId);

                    return (
                      <div
                        key={role.roleId}
                        className={`group relative sm:p-3 md:p-4 rounded-lg sm:rounded-xl border-2 transition-all duration-200 cursor-pointer min-h-[30px]
      ${
        selected
          ? "border-[#5B9BD5] bg-gradient-to-br from-[#5B9BD5]/10 to-[#5B9BD5]/5 shadow-md"
          : "border-gray-200 hover:border-[#5B9BD5]/60 hover:shadow-sm bg-white"
      }`}
                        onClick={() => {
                          if (selected) {
                            setFormRoles(
                              formRoles.filter((r) => r !== role.roleId),
                            );
                          } else {
                            setFormRoles([...formRoles, role.roleId]);
                          }
                        }}
                      >
                        {/* 🔹 Tooltip */}
                        {role.roleDescription && (
                          <div
                            className="absolute -top-3 left-1/2 -translate-x-1/2 -translate-y-full 
                      w-max max-w-[220px] px-3 py-2 text-xs text-white 
                      bg-gray-900 rounded-md shadow-lg 
                      opacity-0 group-hover:opacity-100 
                      transition-opacity duration-200 
                      pointer-events-none z-50"
                          >
                            {role.roleDescription}

                            {/* Tooltip Arrow */}
                            <div
                              className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 
                        w-2 h-2 bg-gray-900 rotate-45"
                            ></div>
                          </div>
                        )}

                        <div className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={selected}
                            readOnly
                            className="w-4 h-4 text-[#5B9BD5]"
                          />

                          <div className="flex-1">
                            <Label className="block text-sm font-semibold text-gray-900">
                              {role.roleName}
                            </Label>
                          </div>
                        </div>

                        {selected && (
                          <div className="absolute top-2 right-2">
                            <CheckCircle className="w-4 h-4 text-[#5B9BD5]" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-2 sm:pb-3 border-b-2 border-[#5B9BD5]/30">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-lg bg-[#5B9BD5]/10 flex items-center justify-center flex-shrink-0">
                      <UserCheck className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-[#5B9BD5]" />
                    </div>
                    <h3 className="text-xs sm:text-sm md:text-base font-semibold text-gray-800">
                      Available for Delegation
                    </h3>
                  </div>

                  <Badge
                    variant="outline"
                    className="border-[#5B9BD5] bg-[#5B9BD5]/10 text-[#5B9BD5] text-[10px] sm:text-xs px-2 sm:px-3 py-0.5 sm:py-1 w-fit"
                  >
                    {
                      formRoles.filter((id) =>
                        delegationRoles.some((r) => r.roleId === id),
                      ).length
                    }{" "}
                    of {delegationRoles.length} selected
                  </Badge>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-2.5 md:gap-3">
                  {delegationRoles.map((role) => {
                    const selected = formRoles.includes(role.roleId);

                    return (
                      <div
                        key={role.roleId}
                        className={`group relative sm:p-3 md:p-4 rounded-lg sm:rounded-xl border-2 transition-all duration-200 cursor-pointer min-h-[30px]
          ${
            selected
              ? "border-[#5B9BD5] bg-gradient-to-br from-[#5B9BD5]/10 to-[#5B9BD5]/5 shadow-md"
              : "border-gray-200 hover:border-[#5B9BD5]/60 hover:shadow-sm bg-white"
          }`}
                        onClick={() => {
                          if (selected) {
                            setFormRoles(
                              formRoles.filter((r) => r !== role.roleId),
                            );
                          } else {
                            setFormRoles([...formRoles, role.roleId]);
                          }
                        }}
                      >
                        {/* 🔹 Tooltip (Hover Description) */}
                        {role.roleDescription && (
                          <div
                            className="absolute -top-3 left-1/2 -translate-x-1/2 -translate-y-full 
              w-max max-w-[220px] px-3 py-2 text-xs text-white 
              bg-gray-900 rounded-md shadow-lg 
              opacity-0 group-hover:opacity-100 
              transition-opacity duration-200 
              pointer-events-none z-50"
                          >
                            {role.roleDescription}
                            <div
                              className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 
              w-2 h-2 bg-gray-900 rotate-45"
                            ></div>
                          </div>
                        )}

                        <div className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={selected}
                            readOnly
                            className="w-4 h-4 text-[#5B9BD5]"
                          />

                          <Label className="block text-sm font-semibold text-gray-900">
                            {role.roleName}
                          </Label>
                        </div>

                        {selected && (
                          <div className="absolute top-2 right-2">
                            <CheckCircle className="w-4 h-4 text-[#5B9BD5]" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ================= SELECTED SUMMARY ================= */}
              {formRoles.length > 0 && (
                <div className="sticky bottom-0 bg-gradient-to-br from-sky-50 to-blue-50 border-2 border-[#5B9BD5]/30 rounded-lg sm:rounded-xl p-2.5 sm:p-3 md:p-4 shadow-lg">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 rounded-lg bg-[#5B9BD5] flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-white" />
                      </div>
                      <h4 className="text-[11px] sm:text-xs md:text-sm font-semibold text-[#5B9BD5]">
                        Selected: {formRoles.length}{" "}
                        {formRoles.length === 1 ? "role" : "roles"}
                      </h4>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormRoles([])}
                      className="text-[10px] sm:text-xs text-gray-600 hover:text-red-600 hover:bg-red-50 h-6 sm:h-7 w-fit px-2 sm:px-3"
                    >
                      Clear All
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {formRoles.map((id) => {
                      const role = rolesFromApi.find((r) => r.roleId === id);

                      return (
                        <Badge
                          key={id}
                          variant="outline"
                          className={`${getRoleColor(role?.roleName || "")} px-1.5 sm:px-2 md:px-3 py-0.5 sm:py-1 md:py-1.5 text-[9px] sm:text-[10px] md:text-xs font-medium flex items-center gap-0.5 sm:gap-1 md:gap-1.5`}
                        >
                          <Shield className="w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 flex-shrink-0" />
                          {role?.roleName}
                          <button
                            onClick={() =>
                              setFormRoles(formRoles.filter((r) => r !== id))
                            }
                          >
                            ×
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 pt-2.5 sm:pt-3 md:pt-4 border-t">
              <p className="text-[9px] sm:text-[10px] md:text-xs text-gray-500 text-center sm:text-left">
                {formRoles.length === 0
                  ? "Please select at least one role"
                  : `${formRoles.length} role(s) selected`}
              </p>
              <div className="flex gap-1.5 sm:gap-2 justify-center sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => setIsRolesMaximized(false)}
                >
                  Close
                </Button>
                <Button
                  onClick={() => setIsRolesMaximized(false)}
                  style={{ backgroundColor: "#5B9BD5" }}
                  className="text-white"
                  disabled={formRoles.length === 0}
                >
                  <CheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 mr-1 sm:mr-1.5 md:mr-2" />
                  Apply
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
